/** Match timing and movement / combat constants */
/** Stand still, both visible, before fog. */
export const SETTLE_MS = 2000;
/** Click-to-move phase length (seconds displayed as countdown). */
export const MANEUVER_SECONDS = 10;
/** Distance moved each countdown second during maneuver. */
export const MANEUVER_STEP = 45;
/** How fast you lerp toward each pace (px/s). Higher = snappier steps. */
export const MANEUVER_LERP_SPEED = 100;
/** Aim/shoot phase length after maneuver ends. */
export const AIM_TIMEOUT_MS = 10_000;
/** Max turn rate while aiming (radians / second). */
export const AIM_TURN_SPEED = 5.0;
export const BULLET_SPEED = 2000;
/** Axis-aligned hit box (taller than wide — matches sprite silhouette). */
export const HIT_BOX_W = 28;
export const HIT_BOX_H = 72;
export const HIT_BOX_HALF_W = HIT_BOX_W / 2;
export const HIT_BOX_HALF_H = HIT_BOX_H / 2;
/**
 * Movement inset so the full 54×84 sprite (and cactus hop) stays on-screen.
 * Larger than the hit box — combat stays the same, walking can't clip the hat.
 */
export const MOVE_HALF_W = 28;
export const MOVE_HALF_H = 54;
/** How often peers send position snapshots (ms). */
export const STATE_SYNC_MS = 50;
/** Full cone aperture while turning (degrees). */
export const MAX_CONE_DEG = 30;
/** Time to bloom from perfect to max spread while continuously turning (seconds). */
export const ACCURACY_BUILD_S = 0.5;
/** Time to recover from max spread to perfect when not turning (seconds). */
export const ACCURACY_RECOVER_S = 0.5;
/** Angular velocity (rad/s) above which you count as "turning". */
export const TURN_SPREAD_EPS = 0.2;
export const MAX_CONE_RAD = (MAX_CONE_DEG * Math.PI) / 180;
export const MAX_SPREAD_HALF_RAD = MAX_CONE_RAD / 2;
/** Full-canvas rectangular arena (landscape). */
export const ARENA_W = 1200;
export const ARENA_H = 675;
export const ARENA_CX = ARENA_W / 2;
export const ARENA_CY = ARENA_H / 2;
/** Host on the left, facing right; guest on the right, facing left. */
export const HOST_SPAWN = {
    x: ARENA_CX - 64,
    y: ARENA_CY,
    facing: 0,
};
export const GUEST_SPAWN = {
    x: ARENA_CX + 64,
    y: ARENA_CY,
    facing: Math.PI,
};
export function clampToArena(x, y, halfW = MOVE_HALF_W, halfH = MOVE_HALF_H) {
    return {
        x: Math.min(ARENA_W - halfW, Math.max(halfW, x)),
        y: Math.min(ARENA_H - halfH, Math.max(halfH, y)),
    };
}
/** Keep a fighter on their own half (cannot cross the center line). */
export function clampToOwnHalf(role, x, y, halfW = MOVE_HALF_W, halfH = MOVE_HALF_H) {
    const p = clampToArena(x, y, halfW, halfH);
    if (role === "host") {
        p.x = Math.min(p.x, ARENA_CX - halfW);
    }
    else {
        p.x = Math.max(p.x, ARENA_CX + halfW);
    }
    return p;
}
/** Bullet vs tall rectangular player hit box (centered on fighter). */
export function pointInHitBox(px, py, fx, fy) {
    return (Math.abs(px - fx) <= HIT_BOX_HALF_W && Math.abs(py - fy) <= HIT_BOX_HALF_H);
}
/**
 * Continuous collision for fast bullets: does the segment (x0,y0)→(x1,y1)
 * intersect an AABB centered at (cx,cy)? Returns the first contact point, or null.
 */
export function segmentHitAabb(x0, y0, x1, y1, cx, cy, halfW, halfH) {
    if (Math.abs(x0 - cx) <= halfW && Math.abs(y0 - cy) <= halfH) {
        return { x: x0, y: y0 };
    }
    const minX = cx - halfW;
    const maxX = cx + halfW;
    const minY = cy - halfH;
    const maxY = cy + halfH;
    const dx = x1 - x0;
    const dy = y1 - y0;
    let tEnter = 0;
    let tExit = 1;
    const clip = (p, q) => {
        if (p === 0)
            return q >= 0;
        const r = q / p;
        if (p < 0) {
            if (r > tExit)
                return false;
            if (r > tEnter)
                tEnter = r;
        }
        else {
            if (r < tEnter)
                return false;
            if (r < tExit)
                tExit = r;
        }
        return true;
    };
    if (!clip(-dx, x0 - minX))
        return null;
    if (!clip(dx, maxX - x0))
        return null;
    if (!clip(-dy, y0 - minY))
        return null;
    if (!clip(dy, maxY - y0))
        return null;
    if (tEnter > tExit)
        return null;
    return { x: x0 + dx * tEnter, y: y0 + dy * tEnter };
}
/**
 * Continuous collision for fast bullets: does the segment (x0,y0)→(x1,y1)
 * intersect the fighter AABB? Returns the first contact point, or null.
 */
export function segmentHitHitBox(x0, y0, x1, y1, fx, fy) {
    return segmentHitAabb(x0, y0, x1, y1, fx, fy, HIT_BOX_HALF_W, HIT_BOX_HALF_H);
}
export function pointInArena(x, y) {
    return x >= 0 && x <= ARENA_W && y >= 0 && y <= ARENA_H;
}
/** Host owns the western half (x < center). */
export function onHostHalf(x) {
    return x < ARENA_CX;
}
/** Guest owns the eastern half (x >= center). */
export function onGuestHalf(x) {
    return x >= ARENA_CX;
}
//# sourceMappingURL=duel.js.map